import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1751921661682-3eba30ea453a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwdGVycmF6em8lMjB0ZXh0dXJlfGVufDB8fHx8MTc4Mzc2NTM3M3ww&ixlib=rb-4.1.0&q=85";

const ROLE_LABELS = {
  admin: { title: "Admin Console", accent: "#C85A42", persona: "Arpan Goel · Co-founder" },
  supervisor: { title: "Supervisor Console", accent: "#5B7B61", persona: "Mohit Gautam · Manager" },
  worker: { title: "Worker App", accent: "#1C1C1A", persona: "Shop floor" },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleHint = params.get("role");
  const meta = ROLE_LABELS[roleHint];
  const [phone, setPhone] = useState("");
  const [credential, setCredential] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(phone.trim(), credential);
      if (roleHint && u.role !== roleHint) {
        toast.error(`This account is a ${u.role}, but you opened the ${roleHint} console. Redirecting to your app.`);
      } else {
        toast.success(`Welcome, ${u.name}`);
      }
      navigate(u.role === "worker" ? "/worker" : "/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 text-white overflow-hidden">
        <img src={BG} alt="terrazzo" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1C1C1A]/70" />
        <div className="relative z-10">
          <div className="text-xs tracking-[0.3em] uppercase text-white/70">Sango Terrazzo</div>
          <div className="font-display text-4xl lg:text-6xl mt-3 tracking-tighter font-black leading-[0.95]">STRONGER.<br/>BETTER.<br/>FOREVER.</div>
        </div>
        <div className="relative z-10 text-xs text-white/60 max-w-md leading-relaxed tracking-wide">
          Near FCI Godown, Village Rasuiya<br/>Bareilly, Uttar Pradesh · 243123<br/>
          <span className="text-white/40 mt-2 inline-block">Premium terrazzo tiles · countertops · washbasins</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F9F9F8]">
        <div className="w-full max-w-sm">
          <Link to="/" className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] hover:text-[#1C1C1A] inline-flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Back to app selector
          </Link>
          {meta && (
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-3 ml-3" style={{ background: `${meta.accent}12`, color: meta.accent }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.accent }} />
              <span className="text-[10px] tracking-[0.25em] uppercase">{meta.title}</span>
            </div>
          )}
          <div className="text-xs tracking-[0.3em] uppercase text-[#6E6E6A] mb-2">Sign in</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black text-[#1C1C1A]">{meta?.title || "Operations Console"}</h1>
          <p className="text-[#6E6E6A] mt-2 text-sm">{meta?.persona ? `For ${meta.persona}` : "Enter your phone and PIN (workers) or password (admin/supervisor)."}</p>

          <form className="mt-8 space-y-5" onSubmit={onSubmit}>
            <div>
              <Label htmlFor="phone" className="text-xs tracking-[0.2em] uppercase text-[#6E6E6A]">Phone</Label>
              <Input
                id="phone" data-testid="login-phone"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="9999999999" className="mt-2 h-12" required
              />
            </div>
            <div>
              <Label htmlFor="cred" className="text-xs tracking-[0.2em] uppercase text-[#6E6E6A]">PIN / Password</Label>
              <Input
                id="cred" data-testid="login-cred"
                type="password" value={credential} onChange={(e) => setCredential(e.target.value)}
                placeholder="••••" className="mt-2 h-12" required
              />
            </div>
            <Button
              data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-12 bg-[#1C1C1A] hover:bg-black text-white group"
            >
              {loading ? "Signing in..." : (<span className="inline-flex items-center gap-2">Enter console <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>)}
            </Button>
          </form>

          <div className="mt-8 border-t border-[#D8D8D5] pt-5 text-xs text-[#6E6E6A] leading-relaxed">
            <div className="tracking-[0.2em] uppercase mb-2">Team accounts</div>
            <div>Arpan Goel (Admin) — <span className="text-[#1C1C1A]">8445950390 / admin123</span></div>
            <div>Mohit Gautam (Supervisor) — <span className="text-[#1C1C1A]">8958541840 / super123</span></div>
            <div className="mt-2 text-[#6E6E6A]/70">Workers are added by admin/supervisor.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
